import { Box, Breadcrumbs, Drawer, Typography, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TableSection from '../../components/layouts/TableSection';
import { AppLink } from '../../components/ui/AppLink';
import { GoTheOtherSiteButton } from '../../components/ui/GoTheOtherSiteButton';
import {
  PageArea,
  Pagination,
  TableArea,
} from '../../components/ui/StyledComponents';
import { UrlTable } from '../../components/ui/UrlTable';
import { PageState, TableSectionState } from '../../features/DataUtil';
import { getHistoryState, setHistoryState } from '../../features/HistoryUtil';
import { useParentPathModel } from '../../features/ParentPathModel';
import { PathData } from '../../features/PathModel';
import {
  createLinkElement,
  LinkValues,
  RaceData,
} from '../../features/RaceDataUtil';
import { createOptions } from '../../features/TableDataUtil';
import { I18n, useI18n } from '../../hooks/I18n';
import { PaginationData, useData } from '../../hooks/UseData';
import { getLogger } from '../../utils/Logger';
import { defined, returnIf } from '../../utils/Util';

const logger = getLogger(import.meta.url);

interface PageProp {
  filePath: string;
}

const getParentPath = (filePath: string): string => {
  const parentPath = filePath.slice(0, filePath.lastIndexOf('/') + 1);
  return parentPath;
};

const PageBreadcrumbs: React.FC<PageProp> = ({ filePath }) => {
  const i18n = useI18n();
  const { fetchRaceData, fetchHeaderData } = useData();

  const raceData = fetchRaceData();
  const pathData = new PathData(filePath);
  // /people/people/ や /achievement/achievement/
  const parentPath = getParentPath(filePath);
  const parentName = new PathData(parentPath).localizeDirName(i18n, raceData);

  if (parentName.displayName.length > 0) {
    // TODO json読み込みはまとめる
    const headerData = fetchHeaderData();
    // ヘッダー定義に含まれるパスをすべて取得
    const headerItemPaths = Object.values(headerData.buttons).reduce<string[]>(
      (ids, items) =>
        typeof items === 'string'
          ? [...ids, items]
          : [...ids, ...items.map((item) => item.path)],
      []
    );

    // リンク先のページがヘッダーから参照されている場合のみ、リンクする
    const dirName = pathData.localizeDirName(i18n, raceData);
    const headerItemPath = headerItemPaths.filter((id) =>
      // achievementの場合は /achievement/achievement、
      // id が /achievement になるため前方一致で比較する必要あり
      // その他の場合は完全一致で比較してよい
      parentPath.startsWith(id)
    )?.[0];
    const element = defined(headerItemPath) ? (
      <AppLink to={headerItemPath}>{parentName.displayName}</AppLink>
    ) : (
      <Typography>{parentName.displayName}</Typography>
    );

    return (
      <Breadcrumbs sx={{ pb: 2 }}>
        {element}
        <Typography>{dirName.displayName}</Typography>
      </Breadcrumbs>
    );
  } else {
    return <></>;
  }
};

const showPagination = (
  filePath: string,
  paginationData: PaginationData
): boolean => {
  const regexp = paginationData.regexHidePagination;
  return !new RegExp(regexp).test(filePath);
};

const PageFooterPagination: React.FC<PageProp> = ({ filePath }) => {
  // /people/people/ や /achievement/achievement/
  const parentPath = getParentPath(filePath);
  const data = useParentPathModel(parentPath);
  const prevFilePath = data.getPrevPath(filePath);
  const nextFilePath = data.getNextPath(filePath);
  return (
    <Pagination
      prevButtonProps={returnIf(prevFilePath, (path) => ({
        label: path.displayName,
        path: path.path,
      }))}
      nextButtonProps={returnIf(nextFilePath, (path) => ({
        label: path.displayName,
        path: path.path,
      }))}
    />
  );
};

const createPageTitle = (
  filePath: string,
  i18n: I18n,
  raceData: RaceData
): string => {
  let title = new PathData(filePath).localizeDirName(
    i18n,
    raceData
  ).displayName;
  const parentPath = getParentPath(filePath);
  const parentName = new PathData(parentPath).localizeDirName(
    i18n,
    raceData
  ).displayName;
  if (parentName.length > 0) {
    title = `${parentName} / ${title}`;
  }
  return i18n.LABELS.localize('page_title', undefined, { title });
};

const Page: React.FC<PageProp> = ({ filePath }) => {
  const { fetchPageData, fetchRaceData, fetchPaginationData } = useData();
  const data = fetchPageData(filePath);
  const raceData = fetchRaceData();
  const theme = useTheme();
  const i18n = useI18n();
  const [footer, setFooter] = useState<React.ReactElement | null>(null);
  const [description, setDescription] = useState<string>('');

  // リンク元が設定したstateよりテーブル名と列名を取得
  const pageState: PageState = getHistoryState();
  logger.debug(`START ${filePath}`);

  const onLinkDisplayed = useCallback((linkValues: LinkValues) => {
    // リンクをテーブル内に表示する以上、現状でlinkValuesのとる値は一つのみ
    const labelId =
      linkValues.raceYear !== undefined
        ? 'page_archived_year'
        : linkValues.raceId !== undefined
        ? 'page_archived_race'
        : 'page_archived_circuit';
    const footer = (
      <>
        <Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {i18n.LABELS.localize(labelId)}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {createLinkElement(linkValues, raceData, i18n)}
        </Typography>
      </>
    );
    setFooter(footer);
  }, []);

  const onFooterClicked = useCallback(() => {
    setFooter(null);
  }, []);

  const onTableSectionStateChanged = useCallback(
    (tableId: string, tableSectionState: TableSectionState) => {
      const previousState = getHistoryState();
      const newState: PageState = {
        ...previousState,
        tableStates: {
          ...previousState.tableStates,
          [tableId]: tableSectionState,
        },
      };
      logger.debug(`Change state to ${JSON.stringify(newState)}`);
      setHistoryState(newState);
    },
    []
  );
  const options = createOptions(data);
  const targetTableId = pageState.initialTable ?? '';
  // スクロール対象のテーブルより上に存在するテーブルID一覧
  const tableIds = options.map((o) => o.tableId);
  const tableRefs = React.useRef<Record<string, HTMLSpanElement>>({});
  const [loadedTablesState, setLoadedTablesState] = React.useState(
    Object.fromEntries(tableIds.map((id) => [id, false]))
  );
  React.useEffect(() => {
    // スクロール対象のテーブルより上のテーブルが全てロードされたらスクロールする
    // スクロール後にデータ読み込みにより縦位置がずれるのを防ぐ
    const readyToScroll = Object.entries(loadedTablesState)
      .filter((v, index) => index < tableIds.indexOf(targetTableId))
      .every(([, value]) => value);
    if (readyToScroll) {
      const table = tableRefs.current[targetTableId];
      if (defined(table)) {
        logger.debug(`Do scroll. Table=${targetTableId}`);
        table.scrollIntoView();
      }
      // スクロール位置をリセットする。ブラウザバック時にスクロールさせないため
      setHistoryState({ ...getHistoryState(), initialTable: undefined });
    }
  }, [loadedTablesState]);

  React.useEffect(() => {
    // ロード済みテーブルの内容の文字列を取得
    const loadedTableTexts = Object.entries(loadedTablesState)
      .filter(([, value]) => value)
      .map(([id]) => id)
      .map((id) => tableRefs.current[id]?.innerText.replace(/\s+/g, ' '));
    setDescription(loadedTableTexts.join(' ').substring(0, 256));
  }, [loadedTablesState]);

  const allLoadedSignForE2ETesting = React.useMemo(() => {
    // 全てのテーブルの読み込みが終わったことをE2Eテストで知るための要素
    const allTablesLoaded = Object.entries(loadedTablesState).every(
      ([, value]) => value
    );
    if (allTablesLoaded) {
      return <Box data-testid="all-tables-loaded"></Box>;
    } else {
      return <></>;
    }
  }, [loadedTablesState]);

  const pathData = new PathData(filePath);
  const title = pathData.localizeDirName(i18n, raceData).displayName;

  const tables: React.ReactElement[] = React.useMemo(
    () =>
      options.map((tableOption) => {
        const { tableId } = tableOption;
        const tableSection = (
          <TableSection
            sx={{ pb: 3 }}
            filePath={filePath}
            table={tableOption}
            initialState={
              pageState.tableStates[tableId] ?? {
                optionsState: {},
                tableState: {},
              }
            }
            onStateChanged={(newState) =>
              onTableSectionStateChanged(tableId, newState)
            }
            onLinkDisplayed={onLinkDisplayed}
            onReadyToScroll={() =>
              setLoadedTablesState((state) => ({
                ...state,
                [tableId]: true,
              }))
            }
          />
        );
        return (
          <div key={tableId}>
            {options.length > 1 ? (
              // ref は HTML タグにしか付与できないのでspanで囲う
              <span
                ref={(element) => {
                  if (defined(element)) {
                    tableRefs.current[tableId] = element;
                  }
                }}
              >
                <TableArea
                  title={i18n.LABELS.localize(tableId, undefined, { title })}
                >
                  {tableSection}
                </TableArea>
              </span>
            ) : (
              tableSection
            )}
          </div>
        );
      }),
    []
  );

  const urlElement = (
    <UrlTable items={pathData.getUrlsInfo(i18n, raceData)} theme={theme} />
  );

  const pagination = React.useMemo(
    () =>
      showPagination(filePath, fetchPaginationData()) ? (
        <PageFooterPagination filePath={filePath} />
      ) : (
        <></>
      ),
    []
  );

  // TODO: 下のローディング表示
  return (
    <>
      <Helmet>
        <title>{createPageTitle(filePath, i18n, raceData)}</title>
        <meta name="description" content={description} />
      </Helmet>
      {allLoadedSignForE2ETesting}
      <PageArea
        title={
          <>
            {title}
            <GoTheOtherSiteButton path={filePath} />
          </>
        }
        breadCrumps={<PageBreadcrumbs filePath={filePath} />}
      >
        {pagination}
        <Box sx={{ mb: 1 }} />
        {urlElement}
        {tables}
        {pagination}
        <Drawer
          anchor={'bottom'}
          onClose={onFooterClicked}
          open={defined(footer)}
          PaperProps={{ sx: { maxHeight: '50%', p: 1 } }}
        >
          {footer}
        </Drawer>
      </PageArea>
    </>
  );
};

export default Page;
